import React, { useMemo } from "react";
import { type FieldPath, type FieldValues } from "react-hook-form";
import { type GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { AppointmentType } from "../../../apis/appointmentApi";
import { sortByName } from "../../../utils/sortUtils";
import { useGetCalendarClassTypesQuery } from "./ClassTypeSelect-generated.hooks";
import { Separator, Button } from "@pushpress/pushpress-ui";
import { Plus } from "lucide-react";
import { useNavigate } from "../../../hooks/useNavigate";

type ClassTypeSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends AppointmentType,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function ClassTypeSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends AppointmentType,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClassTypeSelectProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const navigate = useNavigate();
  const { data } = useGetCalendarClassTypesQuery({
    variables: {
      active: true,
    },
  });

  const options = useMemo(() => {
    if (!data?.getCalendarClassTypes) {
      return [];
    }
    return [...data.getCalendarClassTypes].sort(
      sortByName,
    ) as unknown as Option[];
  }, [data]);

  const menuAdornment = (
    <>
      <Separator />
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-w-full">
        <Button
          variant="ghost"
          onClick={() => navigate("/schedule/class-types")}
          className="tw-w-full"
        >
          <Plus className="tw-w-4 tw-h-4 tw-mr-2" />
          <span>Add class type</span>
        </Button>
      </div>
    </>
  );

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...props}
      options={options}
      enableOptionColor
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.uuid}
      getOptionColor={(option) => option.color}
      menuAdornment={menuAdornment}
    />
  );
}

export default ClassTypeSelect;
